import { formatChange, type KpiDelta, type KpiDeltaTone } from '@platform/design-system';
import type { OverviewTile } from '../overview-tiles.ts';
import { relativeChangeOf, type Change } from '../performance-change.ts';
import type { PerformanceReport } from '../performance.ts';

const OPPOSITE_TONES: Readonly<Record<Change['direction'], KpiDeltaTone>> = {
  up: 'bad',
  down: 'good',
  flat: 'neutral',
};

const changeOf = (tile: OverviewTile, report: PerformanceReport): Change | null => {
  const current = report.comparison === null ? undefined : tile.measure?.(report);
  const previous = report.comparison === null ? undefined : tile.measure?.(report.comparison);
  return current === undefined || previous === undefined ? null : relativeChangeOf(current, previous);
};

const LARGEST_SHOWN_CHANGE = 9.99;

const boundedChangeOf = (amount: number, locale: string): string =>
  Math.abs(amount) > LARGEST_SHOWN_CHANGE
    ? `${amount > 0 ? '>' : '<'} ${formatChange(Math.sign(amount) * LARGEST_SHOWN_CHANGE, locale)}`
    : formatChange(amount, locale);

export const kpiDeltaOf = (
  tile: OverviewTile,
  report: PerformanceReport | undefined,
  describe: { readonly locale: string; readonly label: (value: string) => string },
): KpiDelta | undefined => {
  const change = report === undefined ? null : changeOf(tile, report);
  if (change === null) return undefined;
  const value = boundedChangeOf(change.amount, describe.locale);
  return {
    value,
    direction: change.direction,
    label: describe.label(value),
    ...(tile.risingIs === 'bad' ? { tone: OPPOSITE_TONES[change.direction] } : {}),
  };
};
